import { Box, Button, CloseButton, createListCollection, Text, Drawer, Field, Flex, Portal, Select, Switch, Accordion } from '@chakra-ui/react';
import { countries, FormInput, FormTextarea, Loading, toaster, useDrawer } from '@shared';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetSingleAccount, useUpdateAccount } from '../../api/hooks/useAccountSuperadmin';
import AccountUsersList from './AccountUsersList';

type UpdateAccountForm = {
	name: string;
	accountStatus: AccountStatus;
	businessID: string;
	vatCode: string;
	city: string;
	countryCode: string;
	postalCode: string;
	postalAddress: string;
	creditCount: number;
	isInvoicingEnabled: boolean;
	procountorBusinessPartnerIdentifier: string;
	eInvoiceIntermediateID: string;
	eInvoiceAddress: string;
	invoiceReference: string;
	declineReason: string;
};

export default function AccountDrawer({ accountID }: { accountID: number }) {
	const { t } = useTranslation();
	const { mutate, isPending: isUpdatingAccount } = useUpdateAccount();
	const { closeDrawer } = useDrawer();
	const queryClient = useQueryClient();

	const { data: account, isFetching: isFetchingAccount } = useGetSingleAccount(accountID);

	const methods = useForm<UpdateAccountForm>({
		defaultValues: {
			...account,
		},
	});

	const {
		setValue,
		handleSubmit,
		reset,
		control,
		formState: { dirtyFields, isDirty },
	} = methods;

	useEffect(() => {
		if (account) {
			reset({
				...account,
			});
		}
	}, [account, reset]);

	function updateAccount(values: UpdateAccountForm): void {
		type DirtyKeys = keyof UpdateAccountForm;
		const payload: SuperAdminUpdateAccountRequest = {
			accountID,
			...Object.fromEntries((Object.keys(dirtyFields) as DirtyKeys[]).filter(key => values[key] !== undefined).map(key => [key, values[key]])),
		};

		if (payload.isInvoicingEnabled === false) {
			payload['invoiceDeliveryMethod'] = null;
		}

		mutate(payload, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.saved'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['accounts'] });
				queryClient.refetchQueries({ queryKey: ['account', accountID] });
				closeDrawer('accountDrawer');
			},
		});
	}

	const statusCollection = createListCollection({
		items: [
			{
				label: t('general.waiting_for_approval'),
				value: 'waitingForApproval',
			},
			{
				label: t('general.rejected'),
				value: 'rejected',
			},
			{
				label: t('general.approved'),
				value: 'approved',
			},
			{
				label: t('general.locked'),
				value: 'locked',
			},
		],
	});

	const name = useWatch({ control, name: 'name' });
	const accountStatus = useWatch({ control, name: 'accountStatus' });

	return isFetchingAccount ? (
		<Loading />
	) : (
		<FormProvider {...methods}>
			<form style={{ height: '100%', display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(updateAccount)}>
				<Drawer.Header>
					<Drawer.Title marginRight="20px" wordBreak={'break-word'}>{`${name}`}</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body flex={1}>
					<Accordion.Root multiple collapsible defaultValue={['basicDetails']}>
						<Accordion.Item value={'basicDetails'}>
							<Accordion.ItemTrigger cursor={'pointer'}>
								<Accordion.ItemIndicator />
								{t('general.basic_details')}
							</Accordion.ItemTrigger>
							<Accordion.ItemContent>
								<Accordion.ItemBody>
									<Flex flexDirection={'column'} gap={4} marginBottom={3} width={'100%'}>
										<Field.Root>
											<Field.Label>{t('general.status')}</Field.Label>
											<Select.Root
												zIndex={999}
												collection={statusCollection}
												value={[accountStatus]}
												onValueChange={e => {
													const status = e.value[0] as AccountStatus;

													if (status === 'waitingForApproval') {
														return;
													}

													if (status === 'approved') {
														setValue('declineReason', '', {
															shouldDirty: true,
														});
													}

													setValue('accountStatus', status, {
														shouldDirty: true,
													});
												}}
												variant="outline"
											>
												<Select.HiddenSelect />
												<Select.Control>
													<Select.Trigger data-cy="selectGroupsTrigger">
														<Select.ValueText />
													</Select.Trigger>
													<Select.IndicatorGroup>
														<Select.Indicator />
													</Select.IndicatorGroup>
												</Select.Control>
												<Portal>
													<Select.Positioner zIndex={9999}>
														<Select.Content zIndex={9999}>
															{statusCollection.items.map(item => (
																<Select.Item
																	cursor={item.value === 'waitingForApproval' ? 'not-allowed' : 'pointer'}
																	opacity={item.value === 'waitingForApproval' ? 0.5 : 1}
																	data-cy={`cy-status-${item.value}`}
																	item={item}
																	key={item.value}
																>
																	<Select.ItemText>{item.label}</Select.ItemText>
																	<Select.ItemIndicator />
																</Select.Item>
															))}
														</Select.Content>
													</Select.Positioner>
												</Portal>
											</Select.Root>
										</Field.Root>
										{accountStatus === 'rejected' || accountStatus === 'locked' ? (
											<FormTextarea isRequired label={t('general.decline_reason')} name="declineReason" maxLength={500} />
										) : null}
										<FormInput label={t('general.name')} isRequired name="name" maxLength={100} />
										<FormInput label={t('general.businessID')} isRequired name="businessID" maxLength={50} />
										<FormInput label={t('general.vat_code')} isRequired name="vatCode" maxLength={20} />

										<FormInput label={t('general.postal_address')} isRequired name="postalAddress" maxLength={100} />
										<Flex width={'100%'} gap={2}>
											<FormInput fieldRootProps={{ style: { width: '100%' } }} label={t('general.postal_code')} isRequired name="postalCode" maxLength={20} />
											<FormInput fieldRootProps={{ style: { width: '100%' } }} label={t('general.city')} isRequired name="city" maxLength={50} />
										</Flex>

										<Field.Root required={true}>
											<Field.Label>
												{t('general.country')} <Field.RequiredIndicator />
											</Field.Label>
											<Controller
												rules={{ required: t('general.required') }}
												control={control}
												name={'countryCode'}
												render={({ field, fieldState }) => {
													return (
														<Field.Root invalid={fieldState.invalid}>
															<select
																data-cy="country-select"
																style={{
																	height: '40px',
																	paddingInline: 'var(--tav-spacing-3)',
																	borderRadius: 'var(--tav-radii-l2)',
																	backgroundColor: 'white',
																	border: 'var(--tav-colors-border) 1px solid',
																	width: '100%',
																}}
																value={field.value || ''} // default empty
																onChange={e => field.onChange(e.target.value)}
															>
																<option value="" disabled hidden>
																	{t('general.select')}
																</option>
																{countries.map((c, index) => (
																	<option data-cy={c.code} key={index} value={c.code.toLowerCase()}>
																		{c.name}
																	</option>
																))}
															</select>
															<Field.ErrorText id="errorText">{fieldState.error?.message}</Field.ErrorText>
														</Field.Root>
													);
												}}
											/>
										</Field.Root>
									</Flex>
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>
						<Accordion.Item value={'invoicingDetails'}>
							<Accordion.ItemTrigger cursor={'pointer'}>
								<Accordion.ItemIndicator />
								{t('general.invoicing_details')}
							</Accordion.ItemTrigger>
							<Accordion.ItemContent>
								<Accordion.ItemBody>
									<Flex flexDirection={'column'} gap={4} marginBottom={3} width={'100%'}>
										<Box>
											<Controller
												name="isInvoicingEnabled"
												control={control}
												render={({ field }) => (
													<Field.Root>
														<Switch.Root
															checked={field.value === true}
															onCheckedChange={({ checked }) => {
																if (checked) {
																	field.onChange(true);
																} else {
																	field.onChange(false);
																}
															}}
															name={field.name}
															colorPalette={'brand.primary'}
														>
															<Switch.HiddenInput onBlur={field.onBlur} />
															<Switch.Control />
															<Switch.Label>{t('general.is_invoicing_enabled')}</Switch.Label>
														</Switch.Root>
													</Field.Root>
												)}
											/>
											<Text marginTop={2} userSelect="text" color="gray.600">
												{t('general.this_will_send_emails_to_all_account_users')}
											</Text>
										</Box>

										<FormInput type="email" label={t('general.billing_email')} name="billingEmail" maxLength={100} />
										<FormInput label={t('general.invoice_contact_name')} name="invoiceContactName" maxLength={200} />
										<Box>
											<Text fontWeight="semibold" color="gray.700">
												{t('general.invoice_delivery_method')}
											</Text>
											<Text userSelect="text" color="gray.600">
												{account?.invoiceDeliveryMethod || '-'}
											</Text>
										</Box>

										<FormInput label={t('general.procountor_business_partner_identifier')} name="procountorBusinessPartnerIdentifier" maxLength={40} />
										<FormInput label={t('general.e_invoice_intermediate_id')} name="eInvoiceIntermediateID" maxLength={100} />
										<FormInput label={t('general.e_invoice_address')} name="eInvoiceAddress" maxLength={35} />
										<FormInput label={t('general.invoice_reference')} name="invoiceReference" maxLength={200} />
									</Flex>
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>
						<Accordion.Item value={'users'}>
							<Accordion.ItemTrigger cursor={'pointer'}>
								<Accordion.ItemIndicator />
								{t('general.users')} ({account?.users?.length ?? 0})
							</Accordion.ItemTrigger>
							<Accordion.ItemContent>
								<Accordion.ItemBody>
									<AccountUsersList users={account?.users ?? []} userAccounts={account?.userAccounts ?? []} />
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>
					</Accordion.Root>
				</Drawer.Body>
				<Drawer.Footer>
					<Button onClick={() => closeDrawer('accountDrawer')} variant="outline">
						{t('general.cancel')}
					</Button>
					<Button type="submit" disabled={isUpdatingAccount || !isDirty}>
						{isUpdatingAccount ? t('general.please_wait') : t('general.save')}
					</Button>
				</Drawer.Footer>

				<Drawer.CloseTrigger asChild>
					<CloseButton size="sm" />
				</Drawer.CloseTrigger>
			</form>
		</FormProvider>
	);
}
